import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiChevronDown } from '@mdi/js';

export interface DropdownMenuItem {
  label: string;
  icon?: string;
  onClick?: () => void;
  disabled?: boolean;
  divider?: boolean;
}

interface DropdownMenuProps {
  label: string;
  items: DropdownMenuItem[];
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ label, items }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <Container ref={ref}>
      <Trigger onClick={() => setOpen(o => !o)} $open={open}>
        {label}
        <Icon path={mdiChevronDown} size={0.5} />
      </Trigger>
      {open && (
        <Panel>
          {items.map((item, i) =>
            item.divider ? (
              <MenuDivider key={`div-${i}`} />
            ) : (
              <MenuItem
                key={item.label}
                disabled={item.disabled}
                onClick={() => {
                  if (!item.disabled && item.onClick) {
                    item.onClick();
                    setOpen(false);
                  }
                }}
              >
                {item.icon && <Icon path={item.icon} size={0.55} />}
                <span>{item.label}</span>
              </MenuItem>
            )
          )}
        </Panel>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const Trigger = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: 1px solid ${p => p.$open ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.25)'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Sora', sans-serif;
  background: ${p => p.$open ? 'rgba(255,255,255,0.15)' : 'transparent'};
  color: #ffffff;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  min-width: 200px;
  z-index: 10000;
  overflow: hidden;
  padding: 4px 0;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 14px;
  background: none;
  border: none;
  text-align: left;
  font-size: 13px;
  font-family: 'Sora', sans-serif;
  color: ${p => p.disabled ? '#bdc1c6' : '#333'};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};

  &:hover {
    background: ${p => p.disabled ? 'none' : '#f1f3f4'};
  }

  svg {
    flex-shrink: 0;
    color: ${p => p.disabled ? '#bdc1c6' : '#5f6368'};
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #e8eaed;
  margin: 4px 0;
`;
